import { useState } from "react";
import type { CraftablesIndex } from "@/lib/crafting/types";
import type { SessionInfo } from "@/lib/manifest/client";
import { loadSavedPlans } from "@/lib/crafting/plan";
import type { CatalogueTab } from "@/lib/router";

export interface HomeScreenProps {
  catalogue: CraftablesIndex;
  session: SessionInfo;
  onShapeNew: () => void;
  onBrowse: (tab: CatalogueTab) => void;
}

/**
 * The hub: three ways in. Deliberately just navigation — the actual lists live
 * in the catalogue, which already has the search and filtering an account with
 * dozens of crafted weapons needs.
 */
export function HomeScreen({ catalogue, session, onShapeNew, onBrowse }: HomeScreenProps) {
  // Read once on mount: local storage, and nothing here mutates it.
  const [planCount] = useState(() => loadSavedPlans(window.localStorage).length);

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-8">
      <header className="mb-8">
        <p className="type-tracked text-[0.65rem] text-ink-2">Crafting Planner</p>
        <h1 className="type-display mt-2 text-4xl leading-none sm:text-5xl">Weapon Shaping</h1>
        <p className="mt-3 max-w-xl text-sm text-ink-3">
          Plan a shape or a reshape: what level it needs, how much grind is left, and exactly what
          it costs. Nothing here ever modifies your account.
        </p>
      </header>

      <div className="space-y-3">
        <Card
          title="Shape a New Weapon"
          detail={`Browse all ${catalogue.weaponCount} craftable patterns`}
          onClick={onShapeNew}
        />
        <Card
          title="My Weapons"
          detail={
            session.signedIn
              ? "The weapons you have already shaped, with their real levels"
              : "Sign in with Bungie to plan against a weapon you own"
          }
          onClick={() => onBrowse("owned")}
        />
        <Card
          title="My Plans"
          detail={
            planCount === 0
              ? "Nothing saved yet — plans are stored in this browser"
              : `${planCount} saved plan${planCount === 1 ? "" : "s"}`
          }
          onClick={() => onBrowse("plans")}
        />
      </div>
    </div>
  );
}

function Card({
  title,
  detail,
  onClick,
}: {
  title: string;
  detail: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="corner-brackets group flex w-full items-center justify-between gap-4 border border-line-dim bg-black/40 p-5 text-left transition-colors hover:bg-white/8"
    >
      <span>
        <span className="type-display block text-xl">{title}</span>
        <span className="mt-1 block text-sm text-ink-3">{detail}</span>
      </span>
      <span aria-hidden className="text-2xl text-ink-3 transition-colors group-hover:text-ink">
        →
      </span>
    </button>
  );
}
